/* 
Generated code: do not modify this file as it will be overwritten
Template: ImmutableJSRecord.cst

Known Issues/TODO:
- Using *Props type as Record constructor parameter is insufficient.
If a property needs to be a Record type, it should already be constructed.
- Rename Record fromJS factory method to fromJSON
- Create parseJSON method that translates JSON to Props



 */

/* eslint-disable import/order */

import { createSelector } from '@ngrx/store';
import { List, Map, Record, Seq, Set } from 'immutable';
import { AppState } from 'src/store/appstate';
import { getFromOrThrow, propValueOrDefault, tryOrDefault } from 'src/util/values';

export interface RamcoVehicleConfigurationProps {
  id: string;
  maintenanceSystemId: string;
  tailNumber: string;
  modelNumber: string;
  companyCode: string;
  dateLastUpdated: Date;
  dateCreated: Date;
  dateDeleted: Date | null;
  lastUpdatedById: string | null;
}

export class RamcoVehicleConfigurationRecord extends Record<RamcoVehicleConfigurationProps>({
  id: null as any,
  maintenanceSystemId: null as any,
  tailNumber: null as any,
  modelNumber: null as any,
  companyCode: null as any,
  dateLastUpdated: null as any,
  dateCreated: null as any,
  dateDeleted: null as any,
  lastUpdatedById: null as any,
}) {
  public static fromJS(json: {
    id: string;
    maintenanceSystemId: string;
    tailNumber: string;
    modelNumber: string;
    companyCode: string;
    dateLastUpdated: string | number | Date;
    dateCreated: string | number | Date;
    dateDeleted: string | number | Date | null;
    lastUpdatedById: string | null;
  }): RamcoVehicleConfigurationRecord {
    if (json == null) {
      throw new Error('RamcoVehicleConfigurationRecord.fromJS: parameter cannot be null');
    }
    return new RamcoVehicleConfigurationRecord({
      id: getFromOrThrow(
        json,
        (j) => propValueOrDefault(j, 'id', '00000000-0000-0000-0000-000000000000'),
        'RamcoVehicleConfigurationJson.id',
      ),
      maintenanceSystemId: getFromOrThrow(
        json,
        (j) => j.maintenanceSystemId,
        'RamcoVehicleConfigurationJson.maintenanceSystemId',
      ),
      tailNumber: getFromOrThrow(
        json,
        (j) => j.tailNumber,
        'RamcoVehicleConfigurationJson.tailNumber',
      ),
      modelNumber: getFromOrThrow(
        json,
        (j) => j.modelNumber,
        'RamcoVehicleConfigurationJson.modelNumber',
      ),
      companyCode: getFromOrThrow(
        json,
        (j) => j.companyCode,
        'RamcoVehicleConfigurationJson.companyCode',
      ),
      dateLastUpdated: new Date(json.dateLastUpdated),
      dateCreated: new Date(json.dateCreated),
      dateDeleted: json.dateDeleted != null ? new Date(json.dateDeleted) : null,
      lastUpdatedById: json.lastUpdatedById,
    });
  }

  public static fromJSUnsafe(json: {
    id: string;
    maintenanceSystemId: string;
    tailNumber: string;
    modelNumber: string;
    companyCode: string;
    dateLastUpdated: string | number | Date;
    dateCreated: string | number | Date;
    dateDeleted: string | number | Date | null;
    lastUpdatedById: string | null;
  }): [RamcoVehicleConfigurationRecord, Error[]] {
    if (json == null) {
      throw new Error('RamcoVehicleConfigurationRecord.fromJSUnsafe: parameter cannot be null');
    }
    const errors: Error[] = [];

    const record = new RamcoVehicleConfigurationRecord({
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      id: tryOrDefault(
        () =>
          getFromOrThrow(
            json,
            (j) => propValueOrDefault(j, 'id', '00000000-0000-0000-0000-000000000000'),
            'RamcoVehicleConfigurationJson.id',
          ),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      maintenanceSystemId: tryOrDefault(
        () =>
          getFromOrThrow(
            json,
            (j) => j.maintenanceSystemId,
            'RamcoVehicleConfigurationJson.maintenanceSystemId',
          ),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      tailNumber: tryOrDefault(
        () => getFromOrThrow(json, (j) => j.tailNumber, 'RamcoVehicleConfigurationJson.tailNumber'),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      modelNumber: tryOrDefault(
        () =>
          getFromOrThrow(json, (j) => j.modelNumber, 'RamcoVehicleConfigurationJson.modelNumber'),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      companyCode: tryOrDefault(
        () =>
          getFromOrThrow(json, (j) => j.companyCode, 'RamcoVehicleConfigurationJson.companyCode'),
        (err) => errors.push(err),
      )!,
      dateLastUpdated: new Date(json.dateLastUpdated),
      dateCreated: new Date(json.dateCreated),
      dateDeleted: json.dateDeleted != null ? new Date(json.dateDeleted) : null,
      lastUpdatedById: json.lastUpdatedById,
    });

    return [record, errors];
  }

  constructor(props: RamcoVehicleConfigurationProps) {
    super(props);
    if (props == null) {
      throw new Error('RamcoVehicleConfigurationRecord constructor parameter cannot be null');
    }
  }
}

export abstract class RamcoVehicleConfigurationViewModelBase
  implements RamcoVehicleConfigurationProps
{
  protected readonly _props: RamcoVehicleConfigurationProps;
  private _json?: RamcoVehicleConfigurationProps;

  get id(): string {
    return this._props.id;
  }
  get maintenanceSystemId(): string {
    return this._props.maintenanceSystemId;
  }
  get tailNumber(): string {
    return this._props.tailNumber;
  }
  get modelNumber(): string {
    return this._props.modelNumber;
  }
  get companyCode(): string {
    return this._props.companyCode;
  }

  get dateCreated(): Date {
    return this._props.dateCreated;
  }
  get dateLastUpdated(): Date {
    return this._props.dateLastUpdated;
  }
  get dateDeleted(): Date | null {
    return this._props.dateDeleted;
  }
  get lastUpdatedById(): string | null {
    return this._props.lastUpdatedById;
  }

  constructor(props: RamcoVehicleConfigurationProps) {
    this._props = props;
  }

  public toJS() {
    return this.toJSON();
  }

  public toJSON() {
    if (this._json == null) {
      this._json = {
        id: this._props.id,
        maintenanceSystemId: this._props.maintenanceSystemId,
        tailNumber: this._props.tailNumber,
        modelNumber: this._props.modelNumber,
        companyCode: this._props.companyCode,
        dateLastUpdated: this._props.dateLastUpdated,
        dateCreated: this._props.dateCreated,
        dateDeleted: this._props.dateDeleted,
        lastUpdatedById: this._props.lastUpdatedById,
      };
    }
    return this._json;
  }
}

//#region Selectors

export const selectAllRamcoVehicleConfigurationEntities = (state: AppState) =>
  state.entityStores.ramcoVehicleConfiguration.entities;

export const selectRamcoVehicleConfigurationById = (id: string) => (state: AppState) =>
  state.entityStores.ramcoVehicleConfiguration.entities.get(id);

export const selectRamcoVehicleConfigurationMetadataById = (id: string) => (state: AppState) =>
  state.entityStores.ramcoVehicleConfiguration.meta.get(id);

export const selectRamcoVehicleConfigurationAndMetadataById = (id: string) =>
  createSelector(
    selectRamcoVehicleConfigurationById(id),
    selectRamcoVehicleConfigurationMetadataById(id),
    (ramcoVehicleConfiguration, meta) => ({ ramcoVehicleConfiguration, meta }),
  );

export function selectRamcoVehicleConfigurationEntitiesByIds(ids: string[]) {
  return createSelector(
    selectAllRamcoVehicleConfigurationEntities,

    (entities) => {
      // TODO: Once we're on TS 3.5+, I don't think we'll need use type assertions
      const entries = ids.map((id) => [id, entities.get(id)]) as unknown as Iterable<
        [string, RamcoVehicleConfigurationRecord | undefined]
      >;
      return Map(entries);
    },
  );
}

export function selectRamcoVehicleConfigurationEntitiesByMaintenanceSystemId(
  maintenanceSystemId: string,
) {
  return createSelector(selectAllRamcoVehicleConfigurationEntities, (entities) =>
    entities.filter((e) => e.maintenanceSystemId === maintenanceSystemId).valueSeq(),
  );
}

export function selectRamcoVehicleConfigurationEntitiesByTailNumber(tailNumber: string) {
  return createSelector(selectAllRamcoVehicleConfigurationEntities, (entities) =>
    entities.filter((e) => e.tailNumber === tailNumber).valueSeq(),
  );
}

export function selectRamcoVehicleConfigurationEntitiesByModelNumber(modelNumber: string) {
  return createSelector(selectAllRamcoVehicleConfigurationEntities, (entities) =>
    entities.filter((e) => e.modelNumber === modelNumber).valueSeq(),
  );
}

export function selectRamcoVehicleConfigurationEntitiesByCompanyCode(companyCode: string) {
  return createSelector(selectAllRamcoVehicleConfigurationEntities, (entities) =>
    entities.filter((e) => e.companyCode === companyCode).valueSeq(),
  );
}
//#endregion Selectors
